import { useCallback, useMemo, useState } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import { errorResult, runJob } from '@/lib/http/runJob';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { ToolSummariesResponse, ToolSummary } from '../api/TraceabilityResponseType';

export type ToolSummariesState = {
  /** The company's tools without traces; null while loading the first time or when unavailable. */
  summaries: ToolSummary[] | null;
  isLoading: boolean;
  /** Reloads the list (after a tool was created or its reference changed). */
  refresh: () => void;
};

/**
 * Loads the summaries behind the tool and reference pickers. When the call fails the pickers
 * fall back to plain text fields, so a failure is never shown as an error.
 */
export function useToolSummaries(enabled = true): ToolSummariesState {
  const http = useApiClient();
  const client = useMemo(() => (http ? new TraceabilityApiClient(http) : null), [http]);
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchSummaries = useCallback(
    async (signal: AbortSignal) => {
      if (!client) {
        return errorResult('TraceabilityApiClient not initialized', 'InitializationError');
      }
      return runJob<ToolSummariesResponse>({
        initFn: () => client.toolReadSummaries({ signal }),
        statusFn: (jobId) => client.toolSummariesStatus({ jobId }, { signal }),
        signal,
        fallbackMessage: 'Failed to load tool summaries',
      });
    },
    [client]
  );

  const { data, isLoading } = useApiCall<ToolSummariesResponse>({
    apiFn: fetchSummaries,
    deps: [refreshTick, enabled, client],
    immediate: enabled && client !== null,
  });

  const refresh = useCallback(() => setRefreshTick((tick) => tick + 1), []);

  const summaries =
    enabled && data && Array.isArray(data.toolSummaries) ? data.toolSummaries : null;

  return { summaries, isLoading: enabled && isLoading, refresh };
}
